import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { X, Send, Save, Paperclip } from 'lucide-react';
import toast from 'react-hot-toast';
import { GmailSendMessageRequest } from '../../types/api';

interface ComposeModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialTo?: string;
  initialSubject?: string;
  initialBody?: string;
  initialCc?: string[];
  initialBcc?: string[];
  onSent?: () => void;
  onDraftSaved?: () => void;
  onSendMessage?: (message: GmailSendMessageRequest) => Promise<void>;
  onSaveDraft?: (message: GmailSendMessageRequest) => Promise<void>;
  isSending?: boolean;
  isSavingDraft?: boolean;
}

const Overlay = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.85);
  display: ${props => props.$isOpen ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  z-index: 1100;
  padding: ${props => props.theme.spacing[4]};
`;

const Modal = styled.div`
  width: min(600px, 100%);
  max-height: 90vh;
  background: ${props => props.theme.colors.backgroundSecondary};
  border-radius: ${props => props.theme.borders.radius.xl};
  border: 1px solid ${props => props.theme.colors.border};
  box-shadow: 0 24px 64px rgba(0, 0, 0, 0.45);
  display: flex;
  flex-direction: column;
`;

const Header = styled.div`
  padding: ${props => props.theme.spacing[2]} ${props => props.theme.spacing[4]};
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid ${props => props.theme.colors.border};
  background: ${props => props.theme.colors.backgroundTertiary};
`;

const Title = styled.h2`
  margin: 0;
  font-size: 14px;
  font-weight: ${props => props.theme.typography.fontWeight.semibold};
  color: ${props => props.theme.colors.textPrimary};
`;

const CloseButton = styled.button`
  border: none;
  background: transparent;
  color: ${props => props.theme.colors.textSecondary};
  padding: ${props => props.theme.spacing[1]};
  border-radius: ${props => props.theme.borders.radius.md};
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: ${props => props.theme.colors.backgroundSecondary};
    color: ${props => props.theme.colors.textPrimary};
  }
`;

const Content = styled.div`
  padding: ${props => props.theme.spacing[2]} ${props => props.theme.spacing[4]};
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing[2]};
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Label = styled.label`
  font-size: 11px;
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  color: ${props => props.theme.colors.textSecondary};
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing[2]};
`;

const Input = styled.input`
  width: 100%;
  border-radius: ${props => props.theme.borders.radius.md};
  border: 1px solid ${props => props.theme.colors.border};
  background: ${props => props.theme.colors.background};
  color: ${props => props.theme.colors.textPrimary};
  padding: 6px ${props => props.theme.spacing[3]};
  font-size: 13px;
  transition: border 0.2s;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
    box-shadow: 0 0 0 3px ${props => props.theme.colors.primary}25;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  border-radius: ${props => props.theme.borders.radius.lg};
  border: 1px solid ${props => props.theme.colors.border};
  background: ${props => props.theme.colors.background};
  color: ${props => props.theme.colors.textPrimary};
  padding: 8px ${props => props.theme.spacing[3]};
  font-size: 13px;
  min-height: 80px;
  resize: vertical;
  transition: border 0.2s;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
    box-shadow: 0 0 0 3px ${props => props.theme.colors.primary}25;
  }
`;

const AttachmentSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const FileInput = styled.input`
  display: none;
`;

const UploadButton = styled.label`
  display: inline-flex;
  align-items: center;
  gap: ${props => props.theme.spacing[2]};
  padding: 4px ${props => props.theme.spacing[2]};
  background: ${props => props.theme.colors.background};
  border: 1px dashed ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borders.radius.md};
  color: ${props => props.theme.colors.textPrimary};
  font-size: 11px;
  cursor: pointer;
  transition: all 0.2s;
  align-self: flex-start;

  &:hover {
    border-color: ${props => props.theme.colors.primary};
    background: ${props => props.theme.colors.primary}05;
  }

  svg {
    color: ${props => props.theme.colors.primary};
    width: 12px;
    height: 12px;
  }
`;

const FileList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${props => props.theme.spacing[2]};
`;

const FileChip = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing[2]};
  padding: 2px ${props => props.theme.spacing[2]};
  background: ${props => props.theme.colors.background};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borders.radius.full};
  font-size: 10px;
  color: ${props => props.theme.colors.textPrimary};

  button {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 2px;
    background: transparent;
    border: none;
    cursor: pointer;
    color: ${props => props.theme.colors.textSecondary};
    transition: color 0.2s;

    &:hover {
      color: ${props => props.theme.colors.error};
    }

    svg {
      width: 10px;
      height: 10px;
    }
  }
`;

const Footer = styled.div`
  padding: ${props => props.theme.spacing[2]} ${props => props.theme.spacing[4]};
  border-top: 1px solid ${props => props.theme.colors.border};
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: ${props => props.theme.spacing[2]};
  background: ${props => props.theme.colors.backgroundSecondary};
`;

const Actions = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing[2]};
`;

const Button = styled.button<{ $variant?: 'primary' | 'outline' }>`
  display: inline-flex;
  align-items: center;
  gap: ${props => props.theme.spacing[2]};
  padding: ${props => props.theme.spacing[2]} ${props => props.theme.spacing[3]};
  border-radius: ${props => props.theme.borders.radius.md};
  font-size: 13px;
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  border: 1px solid ${props => props.$variant === 'primary'
    ? props.theme.colors.primary
    : props.theme.colors.border};
  background: ${props => props.$variant === 'primary'
    ? props.theme.colors.primary
    : 'transparent'};
  color: ${props => props.$variant === 'primary'
    ? 'white'
    : props.theme.colors.textSecondary};
  cursor: pointer;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    opacity: 0.9;
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  svg {
    width: 14px;
    height: 14px;
  }
`;

const SecondaryActions = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing[2]};
  align-items: center;
  font-size: 11px;
  color: ${props => props.theme.colors.textSecondary};
`;

export const ComposeModal: React.FC<ComposeModalProps> = ({
  isOpen,
  onClose,
  initialTo,
  initialSubject,
  initialBody,
  initialCc,
  initialBcc,
  onSent,
  onDraftSaved,
  onSendMessage,
  onSaveDraft,
  isSending = false,
  isSavingDraft = false,
}) => {
  const [to, setTo] = useState(initialTo ?? '');
  const [subject, setSubject] = useState(initialSubject ?? '');
  const [body, setBody] = useState(initialBody ?? '');
  const [attachments, setAttachments] = useState<File[]>([]);

  useEffect(() => {
    if (isOpen) {
      setTo(initialTo ?? '');
      setSubject(initialSubject ?? '');
      setBody(initialBody ?? '');
      setAttachments([]);
    }
  }, [isOpen, initialTo, initialSubject, initialBody, initialCc, initialBcc]);

  const closeAndReset = () => {
    setTo('');
    setSubject('');
    setBody('');
    setAttachments([]);
    onClose();
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files);

      // Validate file size (max 10MB per file)
      const validFiles = newFiles.filter(file => {
        if (file.size > 10 * 1024 * 1024) {
          toast.error(`Arquivo ${file.name} é muito grande (máx 10MB)`);
          return false;
        }
        return true;
      });

      setAttachments([...attachments, ...validFiles]);
    }
  };

  const removeFile = (fileToRemove: File) => {
    setAttachments(attachments.filter(file => file !== fileToRemove));
  };

  const processAttachments = async () => {
    return await Promise.all(
      attachments.map(async (file) => {
        return new Promise<{ filename: string; data: string; mimeType: string }>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => {
            const base64Content = (reader.result as string).split(',')[1];
            resolve({
              filename: file.name,
              data: base64Content,
              mimeType: file.type
            });
          };
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
      })
    );
  };

  const handleSend = async () => {
    if (!to.trim()) {
      toast.error('Informe ao menos um destinatário.');
      return;
    }

    if (!onSendMessage) {
      return;
    }

    try {
      const processedAttachments = await processAttachments();

      await onSendMessage({
        to,
        subject,
        body,
        attachments: processedAttachments
      });

      onSent?.();
      closeAndReset();
    } catch (error) {
      console.error('[ComposeModal] Erro ao enviar e-mail', error);
    }
  };

  const handleDraft = async () => {
    if (!to.trim()) {
      toast.error('Informe ao menos um destinatário para salvar o rascunho.');
      return;
    }

    if (!onSaveDraft) {
      return;
    }

    try {
      const processedAttachments = await processAttachments();

      await onSaveDraft({
        to,
        subject,
        body,
        attachments: processedAttachments
      });

      onDraftSaved?.();
      closeAndReset();
    } catch (error) {
      console.error('[ComposeModal] Erro ao salvar rascunho', error);
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <Overlay $isOpen={isOpen} onClick={closeAndReset}>
      <Modal onClick={event => event.stopPropagation()}>
        <Header>
          <Title>Novo e-mail</Title>
          <CloseButton onClick={closeAndReset}>
            <X size={18} />
          </CloseButton>
        </Header>

        <Content>
          <Field>
            <Label>Para</Label>
            <Input
              placeholder="destinatario@empresa.com"
              value={to}
              onChange={event => setTo(event.target.value)}
            />
          </Field>

          <Field>
            <Label>Assunto</Label>
            <Input
              placeholder="Digite o assunto do e-mail"
              value={subject}
              onChange={event => setSubject(event.target.value)}
            />
          </Field>

          <Field>
            <Label>Mensagem</Label>
            <TextArea
              placeholder="Escreva aqui a mensagem..."
              value={body}
              onChange={event => setBody(event.target.value)}
            />
          </Field>

          <AttachmentSection>
            <Label>
              <Paperclip size={14} />
              Anexos (opcional)
            </Label>

            <FileInput
              id="modal-file-upload"
              type="file"
              multiple
              onChange={handleFileUpload}
            />

            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
              <UploadButton htmlFor="modal-file-upload">
                <Paperclip size={14} />
                Adicionar arquivos
              </UploadButton>

              {attachments.length > 0 && (
                <FileList>
                  {attachments.map((file, fileIndex) => (
                    <FileChip key={`new-${fileIndex}`}>
                      {file.name}
                      <button onClick={() => removeFile(file)}>
                        <X />
                      </button>
                    </FileChip>
                  ))}
                </FileList>
              )}
            </div>
          </AttachmentSection>
        </Content>

        <Footer>
          <Actions>
            <Button
              type="button"
              $variant="primary"
              onClick={handleSend}
              disabled={isSending}
            >
              <Send />
              {isSending ? 'Enviando...' : 'Enviar'}
            </Button>
            <Button
              type="button"
              onClick={handleDraft}
              disabled={isSavingDraft}
            >
              <Save />
              {isSavingDraft ? 'Salvando...' : 'Salvar rascunho'}
            </Button>
          </Actions>

          <SecondaryActions>
            As mensagens são enviadas via Gmail API com segurança OAuth2.
          </SecondaryActions>
        </Footer>
      </Modal>
    </Overlay>
  );
};
